import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDrawer } from '@angular/material/sidenav';
import { AIValidationStatuses, GridOverrides, RowSelection } from '@mds/common';
import { MdsIcon } from '@mds/icons';
import {
  GridActionsComponent,
  GridCellAlternativeNodeComponent,
  GridCellIconComponent,
  GridCustomChipsComponent,
  GridModule,
  GridNoRowsComponent
} from '@mds/ui';
import { AnalyzeFiltersService } from '@services/analyze.filters.service';
import { AnalyzeService } from '@services/analyze.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { ReusableService } from '@services/reusable.service';
import { sortBy } from '@utils/sort-by';
import { GridApi, IRowNode, RowDoubleClickedEvent, RowGroupOpenedEvent } from 'ag-grid-community';
import { cloneDeep } from 'lodash';
import { debounceTime, Subject } from 'rxjs';
import {
  menuOptions,
  notApplicableTag,
  rejectedTag,
  validatedTag,
  validationRequiredTag
} from './default-grid-wrapper.model';

@Component({
  selector: 'app-default-grid-wrapper',
  standalone: true,
  imports: [GridModule],
  templateUrl: './default-grid-wrapper.component.html',
  styleUrls: ['./default-grid-wrapper.component.scss']
})
export class DefaultGridWrapperComponent implements OnInit {
  data: any;
  gridApi: GridApi;
  private cdr = inject(ChangeDetectorRef);
  private activeLayer: any;
  private showAllAssociatedData: boolean;
  overrides: GridOverrides;
  gridSettingsLabel = 'app.analyze';
  @Output() gridEvents: EventEmitter<any> = new EventEmitter();
  @Input() drawer: MatDrawer;
  icon = MdsIcon;

  private gridStateChange$ = new Subject<boolean>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly analyzeService = inject(AnalyzeService);
  private readonly analyzeFiltersService = inject(AnalyzeFiltersService);
  private readonly globalDataService = inject(GlobalDataService);
  private readonly reusableService = inject(ReusableService);
  private readonly eventDelegatorService = inject(EventDelegatorService);

  loading = false;
  ngxEventBus = [];
  private pendingExpandedNodeIds = new Set<string>();
  private restoringExpandedNodeId: string | null = null;
  ngOnInit(): void {
    this.analyzeService
      .getAnalyzeData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        setTimeout(() => {
          if (!res) return;

          this.data = undefined;
          this.setActiveLayer();
          this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();
          this.data = cloneDeep(res);

          if (this.gridApi) {
            this.initGridRows();
          } else if (!this.gridApi) {
            this.getGridOverrides();
            this.initGridRows();
          }
          setTimeout(() => {
            this.setGridState();
          }, 10);
        });
      });
    this.analyzeService.gridDrillIn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) return;
      this.drillDown(value);
    });
    this.reusableService.productValidationChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.updateSelectedRowStatus(value);
    });

    this.ngxEventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        if (ev.action === 'toggle-all-switch') {
          this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();
          this.setActiveLayer();
          this.initGridRows();
        }
      })
    );

    this.gridStateChange$.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.gridApi) return;
      const expandedRowIds = this.getExpandedRowIds();
      const gridSetting = {
        columnState: this.gridApi?.getColumnState(),
        filterSettings: this.gridApi?.getFilterModel(),
        pivotMode: this.gridApi?.isPivotMode(),
        expandedNodes: expandedRowIds
      };
      this.globalDataService.setGridStatus(this.gridSettingsLabel, gridSetting);
    });
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
    this.setGridState();

    this.gridApi?.setGridOption('onRowGroupOpened', (event: RowGroupOpenedEvent) => {
      this.onRowGroupOpened(event);
    });
  }

  updateSelectedRowStatus(product: any) {
    this.loading = false;
    if (!product) return;
    const node = this.gridApi.getSelectedNodes()?.[0];
    if (node) {
      const updatedData = {
        ...node.data,
        validation_status: product.validation_status
      };
      node.setData(updatedData);
    }
  }

  gridActionsEvent({ event, node }): void {
    const eventData = {
      rowId: node.data.id,
      data: node.data,
      type: 'action',
      action: event,
      controller: window.location.pathname
    };
    this.gridEvents.emit(eventData);
  }

  gridChangeStatusEvent({ event, node }, status) {
    const eventData = {
      rowId: node.data.id,
      data: node.data,
      type: 'updateAIValidationStatus',
      action: event,
      status: status
    };
    this.gridEvents.emit(eventData);
  }

  initGridRows() {
    const oldRowData = [];
    this.gridApi?.forEachNodeAfterFilterAndSort((node) => {
      oldRowData.push(node.data);
    });
    const newRowData = this.analyzeFiltersService
      .getFilteredRows(
        this.data.nodes.slice(),
        this.activeLayer,
        this.showAllAssociatedData,
        this.data.toggle_on_field,
        true
      )
      .map((row) => {
        // NOTE: this is a hack to fix instances where the root element hasn't got the parents property populated
        return { ...row, parents: row.parents.length ? row.parents : [row.id] };
      });

    if (newRowData.some((row) => row.bom_line)) {
      sortBy(newRowData, 'bom_line');
    }

    setTimeout(() => {
      this.gridApi?.applyTransaction({
        add: newRowData,
        remove: oldRowData
      });

      this.setGridState();

      setTimeout(() => {
        this.gridApi?.refreshCells({
          columns: ['actions'],
          force: true
        });
      });
    }, 10);
  }

  getHeader(node, schema) {
    sortBy(schema, 'sortlevel');
    const headers = [];
    if (!schema) return headers;
    schema.forEach((e) => {
      const colObj: Record<string, any> = {
        field: e.key,
        headerName: e.value || e.label,
        hide: e.hidden || false
      };
      colObj.minWidth = 150;
      if (['in', 'out'].includes(colObj.field)) {
        colObj.minWidth = 350;
      }
      if (e.key === 'validation_status') {
        colObj.minWidth = 180;
        colObj.cellRenderer = GridCustomChipsComponent;
        colObj.drawer = this.drawer;
        colObj.cellRendererParams = {
          onMenuClick: (params: { event: string; node: IRowNode }, status: AIValidationStatuses) => {
            this.loading = true;
            this.gridChangeStatusEvent(params, status);
          },
          getFieldTag: this.getAIFieldTag.bind(this),
          getMenuOptions: this.getMenuOptions.bind(this)
        };
      }
      let fieldValue;
      if (node[colObj.field]) {
        fieldValue = node[colObj.field];
      }
      if (typeof fieldValue === 'number') {
        colObj.filter = 'agNumberColumnFilter';
        if (this.data.layer !== 'costing') {
          colObj.valueFormatter = (params) => {
            return !params.value ? 0 : params.value;
          };
        }
        colObj.comparator = (num1, num2) => {
          if (num1 === null && num2 === null) return 0;
          if (num1 === null || isNaN(num1)) return -1;
          if (num2 === null || isNaN(num2)) return 1;
          return num1 - num2;
        };
      }
      if (typeof fieldValue === 'string') {
        colObj.filter = 'agTextColumnFilter';
      }
      if (!['name', 'original_name'].includes(colObj.field) && colObj?.headerName !== '') {
        headers.push(colObj);
      }
    });

    const eObj = {
      headerName: 'Action',
      key: 'actions',
      pinned: 'left',
      minWidth: 120,
      cellRenderer: GridActionsComponent,
      cellRendererParams: {
        onClick: (params: { event: string; node: IRowNode }) => {
          if (params.event === 'pin_applied') return;
          this.gridActionsEvent(params);
        }
      }
    };
    if (this.data.layer !== 'costing') {
      headers.push(eObj);
    }
    return headers;
  }

  getAIFieldTag(value) {
    const aiGenerated = value?.ai_generated;
    const aiValidationStatus = value?.validation_status;
    let selectedTag = null;
    if (aiGenerated && aiValidationStatus === 'pending') {
      selectedTag = validationRequiredTag;
    } else if (aiGenerated && aiValidationStatus === 'validated') {
      selectedTag = validatedTag;
    } else if (aiGenerated && aiValidationStatus === 'rejected') {
      selectedTag = rejectedTag;
    } else {
      selectedTag = notApplicableTag;
    }
    return selectedTag;
  }

  getMenuOptions(value) {
    const aiGenerated = value?.ai_generated;
    if (!aiGenerated) return null;
    const aiValidationStatus = value?.validation_status;
    const aiMenuOptions = cloneDeep(menuOptions);
    const updatingMenu = aiMenuOptions.find((item) => item.value === aiValidationStatus);
    updatingMenu.disabled = true;
    return aiMenuOptions;
  }

  setActiveLayer() {
    this.globalDataService
      .getActiveApp()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.activeLayer = data;
      });
  }

  onRowDoubleClicked(event: RowDoubleClickedEvent) {
    const gridEventData = {
      event,
      data: event.data,
      type: 'rowDoubleClick',
      controller: window.location.pathname
    };
    this.gridEvents.emit(gridEventData);
  }

  onRowGroupOpened(event: RowGroupOpenedEvent) {
    if (!event.node?.expanded || !event.data) return;
    if (!this.pendingExpandedNodeIds.has(event.node.id)) return;
    if (this.restoringExpandedNodeId) return;

    if (!this.hasLoadedChildRows(event.node)) {
      this.requestExpandedNodeRestore(event.node);
    }
  }

  onSelectionChange() {
    const obj = {
      rows: this.gridApi.getSelectedRows(),
      nodes: this.gridApi.getSelectedNodes(),
      type: 'selection',
      controller: window.location.pathname
    };
    this.gridEvents.emit(obj);
  }

  drillDown(event: any) {
    const row = event.node;
    const { data } = event;
    const { isCostTable } = event;
    const rowId = this.getNodeId(row);
    const restoredRowNode = rowId ? this.gridApi?.getRowNode(rowId) : null;

    const parentsArray = row.parents;
    const nodesData = cloneDeep(data.nodes || data.rows);
    const parentsId = row.id || row.rid;

    if (nodesData[0]?.rid === row.rid && !isCostTable) {
      // root node and the drilled node are the same then remove the root node - or remove a duplicate node from the list and update the parents of nodes
      const removeNode = nodesData.splice(0, 1)[0];
      nodesData.forEach((e) => {
        e.parents = e.parents.filter((p) => p !== removeNode.id);
      });
    }

    if (!this.data) return;
    const drillRows = isCostTable
      ? nodesData
      : this.analyzeFiltersService.getFilteredRows(
          nodesData,
          this.activeLayer,
          this.showAllAssociatedData,
          this.data.toggle_on_field,
          true
        );

    drillRows.forEach((e) => {
      const nodeParents = e.parents;
      nodeParents.forEach((parent) => {
        parent = `${parentsId}.${parent}`;
      });

      e.parents = parentsArray.concat(nodeParents);
    });
    const tx = { add: drillRows };
    this.gridApi.applyTransaction(tx);

    if (this.restoringExpandedNodeId === rowId) {
      if (this.hasLoadedChildRows(restoredRowNode)) {
        this.pendingExpandedNodeIds.delete(rowId);
      } else {
        this.pendingExpandedNodeIds.delete(rowId);
        restoredRowNode?.setExpanded(false);
      }
      this.restoringExpandedNodeId = null;
    }

    if (this.pendingExpandedNodeIds.size) {
      this.expandNodes([...this.pendingExpandedNodeIds]);
    }

    if (this.gridApi) {
      const allCols = this.gridApi.getAllGridColumns();
      if (allCols) {
        this.gridApi.autoSizeColumns(allCols.map((col) => col.getColId()));
      }
    }
  }

  private getGridOverrides() {
    const nodes = this.data.nodes.slice();
    const header = this.getHeader(nodes[0], this.data.gridSchema);

    if (nodes.length > 0) {
      for (const i in header) {
        if (header[i].headerName === 'Amount') {
          header[i].valueFormatter = (params) => {
            return !params.value ? 0 : params.value;
          };
        } else if (header[i].headerName === 'Risk score') {
          header[i].cellRenderer = 'RiskScoreRendererComponent';
        } else if (header[i].headerName === 'Node Type') {
          header[i].cellRenderer = GridCellAlternativeNodeComponent;
        }
      }
      this.overrides = {
        columnDefs: header,
        rowData: [],
        defaultColDef: {
          flex: 1,
          minWidth: 100,
          enableValue: true,
          enablePivot: true,
          sortable: true,
          filter: true,
          resizable: true
        },
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                suppressPivotMode: true
              }
            }
          ]
        },
        autoGroupColumnDef: {
          headerName: 'Name',
          field: 'name',
          minWidth: 500,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: GridCellIconComponent
          },
          filter: 'agTextColumnFilter'
        },
        suppressGroupRowsSticky: true,
        rowSelection: RowSelection.SINGLE,
        treeGrid: true,
        getDataPath: (data) => data.parents,
        getRowId: (params) => (params.data.edge_path || [params.data.rid]).join('-'),
        noRowsOverlayComponent: GridNoRowsComponent
      };
    }
    this.cdr.markForCheck();
  }

  setGridState(): void {
    const gridSettings = this.globalDataService.getGridStatus(this.gridSettingsLabel);
    if (!gridSettings || !this.gridApi) {
      return;
    }
    setTimeout(() => {
      const { filterSettings, columnState, pivotMode, expandedNodes } = gridSettings;
      if (filterSettings) this.gridApi.setFilterModel(filterSettings);
      if (columnState) this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
      if (pivotMode) this.gridApi.setGridOption('pivotMode', pivotMode);
      if (expandedNodes) {
        this.expandNodes(expandedNodes);
      }
    }, 500);
  }

  onGridStateChanged(): void {
    this.gridStateChange$.next(true);
  }

  expandNodes(nodeIds: string[]) {
    this.pendingExpandedNodeIds = new Set(nodeIds || []);
    let nextNodeToRestore: IRowNode<any> | null = null;

    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
      if (!this.pendingExpandedNodeIds.has(node.id)) return;

      node.setExpanded(true);

      if (this.hasLoadedChildRows(node)) {
        this.pendingExpandedNodeIds.delete(node.id);
        if (this.restoringExpandedNodeId === node.id) {
          this.restoringExpandedNodeId = null;
        }
        return;
      }

      if (!this.restoringExpandedNodeId && !nextNodeToRestore) {
        nextNodeToRestore = node;
      }
    });

    if (nextNodeToRestore) {
      this.requestExpandedNodeRestore(nextNodeToRestore);
    }

    this.gridApi.redrawRows();
  }

  private getExpandedRowIds(): string[] {
    const expandedRowIds: string[] = [];
    this.gridApi?.forEachNode((node) => {
      if (node.expanded && this.hasLoadedChildRows(node)) expandedRowIds.push(node.id);
    });
    return expandedRowIds;
  }

  private hasLoadedChildRows(node: IRowNode<any> | null | undefined): boolean {
    return !!node?.childrenAfterGroup?.length;
  }

  private requestExpandedNodeRestore(node: IRowNode<any>): void {
    if (!node.data || this.restoringExpandedNodeId) return;
    if (!this.canRestoreNode(node.data)) {
      this.pendingExpandedNodeIds.delete(node.id);
      node.setExpanded(false);
      return;
    }

    this.restoringExpandedNodeId = node.id;
    this.gridEvents.emit({
      data: node.data,
      type: 'restoreExpand',
      controller: window.location.pathname
    });
  }

  private canRestoreNode(nodeData: any): boolean {
    const activePins = this.analyzeService.getPins();
    return !(activePins.length === 0 && nodeData.in_process === null);
  }

  private getNodeId(nodeData: any): string | null {
    if (!nodeData) return null;
    return (nodeData.edge_path || [nodeData.rid])?.join('-') ?? null;
  }
}
